import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, Sprout, BookOpen, HandHeart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";
const OPAD_COLLAGE = "https://customer-assets.emergentagent.com/job_fa85df46-347d-469f-8cb9-d6bc46d2cb89/artifacts/2c15rs1l_ChatGPT%20Image%20Feb%202%2C%202026%2C%2004_53_33%20AM.png";

export default function OverviewPage() {
  return (
    <div data-testid="overview-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#7BC4E2]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
              Overview
            </h1>
            <p className="text-xl text-white/90">
              Learn about OPAD's mission, vision, and global impact
            </p>
          </motion.div>
        </div>
      </section>

      {/* OPAD Collage Image */}
      <section className="relative py-12 overflow-hidden bg-[#FDF8F4]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src={OPAD_COLLAGE}
              alt="OPAD - Hope in Action: Empowering Communities, Fighting Hunger, Building Skills, Global Advocacy, Real Impact"
              className="w-full max-w-5xl mx-auto rounded-3xl shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#7BC4E2]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#E08B7A]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/80 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
              <p className="text-slate-700 leading-relaxed mb-6 text-lg">
                The <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong> is a registered international non-governmental organization (INGO) committed to empowering vulnerable communities through inclusive development, humanitarian assistance, climate action, and youth-focused capacity-building initiatives across multiple regions worldwide.
              </p>
              
              <p className="text-slate-700 leading-relaxed mb-6 text-lg">
                Headquartered in <strong>Stockholm, Sweden</strong>, OPAD operates through a coordinated global network of regional offices, country teams, and community-based field units across <span className="text-[#7BC4E2] font-semibold">Africa, Europe, Asia, North America, and South America</span>. Our programmes are designed to address the root causes of poverty, inequality, and social exclusion—while promoting sustainable livelihoods, gender equality, digital inclusion, and environmental resilience.
              </p>
              
              <p className="text-slate-700 leading-relaxed mb-8 text-lg">
                Through strategic partnerships with governments, civil society organizations, academic institutions, and international development agencies, OPAD implements locally driven solutions that are aligned with the <strong className="text-[#E08B7A]">United Nations Sustainable Development Goals (SDGs)</strong> and international best practices.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Areas of Intervention */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#E08B7A]/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#4A7C59]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Our Core Areas of Intervention
            </h2>
            <div className="w-24 h-1 bg-[#E08B7A] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#E08B7A] shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <HandHeart className="w-5 h-5 text-[#E08B7A]" />
                Humanitarian Response & Community Relief
              </h4>
              <p className="text-slate-600 text-sm">Delivering emergency assistance and social support to underserved populations affected by crisis and displacement.</p>
            </motion.div>
            
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#7BC4E2] shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#7BC4E2]" />
                Youth Empowerment & Skills Development
              </h4>
              <p className="text-slate-600 text-sm">Supporting young people through vocational training, digital literacy, leadership development, and employment pathways.</p>
            </motion.div>
            
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#4A7C59] shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Sprout className="w-5 h-5 text-[#4A7C59]" />
                Climate Action & Environmental Sustainability
              </h4>
              <p className="text-slate-600 text-sm">Promoting climate resilience through sustainable agriculture, environmental education, and community adaptation strategies.</p>
            </motion.div>
            
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#E5B75F] shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#E5B75F]" />
                Gender Equality & Social Inclusion
              </h4>
              <p className="text-slate-600 text-sm">Advancing inclusive participation for women, girls, and marginalized groups in socio-economic development.</p>
            </motion.div>
            
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#7BC4E2] shadow-lg lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#7BC4E2]" />
                Education & Capacity Building
              </h4>
              <p className="text-slate-600 text-sm">Enhancing access to quality education, lifelong learning opportunities, and institutional strengthening programmes.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Commitment */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Our Global Commitment
            </h2>
            <p className="text-white/90 text-lg mb-6">
              OPAD remains dedicated to delivering impactful, community-led programmes that create opportunities, strengthen resilience, and foster sustainable development across the regions we serve.
            </p>
            <p className="text-[#E5B75F] font-semibold text-xl mb-8">
              Through innovation, collaboration, and accountability, we continue to advance our mission of building hope and creating opportunities for those who need it most.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved">
                <Button className="bg-white hover:bg-slate-100 text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Get Involved
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
